import type { VehicleDef } from './types';

/*
 * Derived numbers for the vehicles page's ladder comparison (lurerun.html "The vehicle ladder"): the drive for a distance (a team move:
 * distance x the detour factor at the road speed, MoveService.Plan), the margin over a runner, the grab time for a full load, and the
 * lend rules. Display only: the server plans the real trip.
 */

/** `teamRules.detourFactor`'s default (ADR 0020). */
export const DETOUR_FACTOR = 1.3;
/** Walking speed for a team move (`teamRules.travelSpeedKmh`). */
export const WALK_KMH = 5;
/** The fallback runner speed when the draft has no `runner` zombie (zombies[] content). */
export const RUNNER_MPS = 4.3;

const n = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Seconds to drive `km` at `kmh` with the detour factor; null when the vehicle can't move. */
export function driveSeconds(km: number, kmh: number, detour = DETOUR_FACTOR): number | null {
  return kmh > 0 ? (km * detour * 3600) / kmh : null;
}

/** "2:40", "19 min", "1 h 05 m". */
export function fmtDuration(s: number | null): string {
  if (s == null || !Number.isFinite(s)) return '-';
  const r = Math.round(s);
  if (r < 600) return `${Math.floor(r / 60)}:${String(r % 60).padStart(2, '0')}`;
  const m = Math.round(r / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} m`;
}

export type LadderRow = {
  id: string;
  label: string;
  seats: number;
  gunners: number;
  topSpeedMps: number;
  /** Top speed over the runner's, m/s (negative = a runner catches it). */
  runnerMarginMps: number;
  driveS: number | null;
  walkS: number;
  tripEnergy: number;
  grabS: number;
  canRam: boolean;
  noise: { idleM: number; fullM: number; hornM: number; gunM: number };
  unlock: number;
};

/** One comparison row per vehicle, in ladder order (the draft's order). `km` is the comparison trip (1 km). */
export function ladderRows(
  vehicles: VehicleDef[],
  opts: {
    km?: number;
    runnerMps?: number;
    grab?: { baseS?: number; perKgS?: number };
    /** An item's noise radius (`items[].noiseRadiusM`), for the weapons a vehicle lends. */
    itemNoiseM?: (itemId: string) => number;
  } = {},
): LadderRow[] {
  const km = opts.km ?? 1;
  const runner = opts.runnerMps ?? RUNNER_MPS;
  const baseS = opts.grab?.baseS ?? 2.5;
  const perKgS = opts.grab?.perKgS ?? 0.03;
  return vehicles.map((v) => {
    const seats = v.seats ?? [];
    return {
      id: v.id ?? '',
      label: v.nickname ? `${v.name || v.id} (${v.nickname})` : v.name || v.id || '',
      seats: seats.length,
      gunners: seats.filter((s) => s && s.role !== 'driver').length,
      topSpeedMps: n(v.topSpeedMps),
      runnerMarginMps: n(v.topSpeedMps) - runner,
      driveS: driveSeconds(km, n(v.roadKmh)),
      walkS: driveSeconds(km, WALK_KMH)!,
      tripEnergy: n(v.tripEnergyPerKm) * km,
      grabS: baseS + perKgS * n(v.cargoKg),
      canRam: n(v.ram?.minSpeedMps) > 0,
      noise: {
        idleM: n(v.noise?.idleM),
        fullM: n(v.noise?.fullM),
        hornM: n(v.noise?.hornM),
        // A mount weapon is a plain WeaponDef with no noise field; the lent items carry theirs.
        gunM: Math.max(
          0,
          ...Object.values(v.lendsWeapons ?? {}).map((id) => opts.itemNoiseM?.(id) ?? 0),
        ),
      },
      unlock: n(v.unlockAfterLureRuns),
    };
  });
}
